// apps/web/src/components/UserManagementPage/ApiKeysView.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Space,
  Popconfirm,
  Typography,
  Alert,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  CopyOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { createApiKey, listApiKeys, revokeApiKey } from "../../api/apiKeyApi";

const { Text } = Typography;
const { Option } = Select;

// Self-contained table chrome matching the User-list table (rounded container,
// #fafafa header, Inter, hairline cell borders).
const StyledApiKeyTable = styled(Table)`
  .ant-table {
    font-family: "Inter", sans-serif;
  }
  .ant-table-container {
    border: 0.5px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background: #fafafa;
    color: #27272a;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr > td {
    font-family: "Inter", sans-serif;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.85);
    padding: 0.625rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.06);
  }
  .ant-table-tbody > tr:hover > td {
    background: #f8fafc;
  }
  .ant-table-tbody > tr.ant-table-placeholder > td {
    color: #a1a1aa;
  }
  .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent;
  }
`;

// Client-facing scopes only — the machine-to-machine `pipeline` group is never
// offered here (only Dashmote staff can mint a pipeline key, in the admin
// portal). Kept in sync with VALID_ENDPOINT_GROUPS in apps/api.
const ENDPOINT_GROUPS = [
  { value: "stores", label: "Stores (outlet search)" },
  { value: "menu", label: "Menu (outlet menus)" },
  { value: "beverages", label: "Beverages (portfolio)" },
  { value: "analytics", label: "Analytics (pricing, compare)" },
  { value: "meta", label: "Meta (filter options)" },
  { value: "market", label: "Market intelligence (premium)" },
  { value: "reviews", label: "Review insights (premium)" },
  { value: "social", label: "Social signals (premium)" },
];

const EXPIRY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "Never" },
];

// The create-modal open state is lifted to the page so the "Create API Key"
// button can live in the shared controls row (alongside the view toggle),
// rather than in a second toolbar row inside this view.
const ApiKeysView = ({ createModalOpen = false, onCreateOpenChange = () => {} }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [form] = Form.useForm();

  // This page is workspace-scoped: keys are always for the selected workspace,
  // and the backend returns only that workspace's customer keys (staff-aware
  // fallback mirrors the roster fetch in useUserData).
  const selectedWorkspace = useSelector(
    (state) => state.auth?.selectedWorkspace
  );
  const authIdClient = useSelector((state) => state.auth?.id_client);
  const idClient = selectedWorkspace?.key ?? authIdClient ?? null;

  const fetchKeys = useCallback(async () => {
    if (!idClient) {
      setKeys([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listApiKeys(idClient);
      setKeys(data.keys || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [idClient]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (values) => {
    try {
      // Keys minted from the client product are always customer-owned and
      // scoped to the current workspace — even when a Dashmote staffer creates
      // one here (staff mint internal keys from the admin portal instead). The
      // backend enforces both, but sending them keeps a staff-created key
      // visible on this very tab rather than defaulting to internal.
      const result = await createApiKey({
        ...values,
        id_client: idClient,
        managed_by: "customer",
      });
      setNewKeyResult(result);
      onCreateOpenChange(false);
      form.resetFields();
      fetchKeys();
      message.success("API key created");
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      fetchKeys();
      message.success("API key revoked");
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Scopes",
      dataIndex: "allowed_endpoints",
      key: "allowed_endpoints",
      render: (endpoints) => {
        const list = endpoints || [];
        if (list.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={list.join(", ")}>
            <Tag style={{ borderRadius: 9999, cursor: "default" }}>
              {list.length} {list.length === 1 ? "scope" : "scopes"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "Never"),
    },
    {
      title: "Requests",
      dataIndex: "total_requests",
      key: "total_requests",
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Last Used",
      dataIndex: "last_used_at",
      key: "last_used_at",
      render: (val) => (val ? new Date(val).toLocaleString() : "Never"),
    },
    {
      title: "Key",
      dataIndex: "key_suffix",
      key: "key_suffix",
      render: (suffix) => (
        <Text code style={{ whiteSpace: "nowrap" }}>
          ••••{suffix}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Copy key ID">
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              aria-label="Copy key ID"
              onClick={() => {
                navigator.clipboard.writeText(record.key_id);
                message.success("Key ID copied");
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Revoke this API key?"
            description="This action cannot be undone."
            onConfirm={() => handleRevoke(record.key_id)}
            okText="Revoke"
            okType="danger"
          >
            <Tooltip title="Revoke">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                aria-label="Revoke API key"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <StyledApiKeyTable
        dataSource={keys}
        columns={columns}
        rowKey="key_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: loading ? null : "No API keys created yet" }}
      />

      {/* Create Key Modal — the workspace is fixed to the current one. */}
      <Modal
        title="Create API Key"
        open={createModalOpen}
        onCancel={() => onCreateOpenChange(false)}
        onOk={() => form.submit()}
        okText="Create"
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Enter a label" }]}
          >
            <Input placeholder="e.g. Claude Code integration" />
          </Form.Item>
          <Form.Item
            name="allowed_endpoints"
            label="Allowed Endpoints"
            rules={[{ required: true, message: "Select at least one endpoint" }]}
          >
            <Select mode="multiple" placeholder="Select endpoint groups">
              {ENDPOINT_GROUPS.map((ep) => (
                <Option key={ep.value} value={ep.value}>
                  {ep.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="expires_in_days" label="Expiry" initialValue={30}>
            <Select>
              {EXPIRY_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* New Key Result Modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined />
            API Key Created
          </Space>
        }
        open={!!newKeyResult}
        onCancel={() => setNewKeyResult(null)}
        footer={[
          <Button key="close" onClick={() => setNewKeyResult(null)}>
            Close
          </Button>,
        ]}
      >
        {newKeyResult && (
          <>
            <Alert
              message="Copy this key now — it will not be shown again."
              type="warning"
              showIcon
              className="mb-4"
            />
            <div className="bg-gray-50 p-3 rounded font-mono text-sm break-all">
              {newKeyResult.key}
            </div>
            <Button
              icon={<CopyOutlined />}
              className="mt-2"
              onClick={() => {
                navigator.clipboard.writeText(newKeyResult.key);
                message.success("Key copied to clipboard");
              }}
            >
              Copy to clipboard
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApiKeysView;
